import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, DoorOpen, Lock, Settings, KeyRound, Building2, Clock, CheckCircle, MapPin, Accessibility } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { siteConfig, servicePages, commercialCards } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';
import TrustStrip from '../components/TrustStrip';

const iconMap = { DoorOpen, Lock, Settings, KeyRound, Building2, Accessibility };

const featuredProjects = [
  { title: 'North Shore Hospital', detail: 'Maglocks installed across the site' },
  { title: 'BNZ Head Office, Queen Street', detail: 'Door hardware & access control installation' },
  { title: 'Pukekohe Train Station', detail: 'Mechanical & electronic hardware, door automation' },
];

const Commercial = () => {
  usePageMeta({
    title: 'Commercial Locksmiths & Door Hardware Auckland | Shore Doors Locksmiths',
    description:
      'Commercial door hardware, access control installation, maintenance and master key systems — from the install team trusted on Auckland hospitals, banks and schools.',
  });

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src="/images/projects/auckland-city-hospital.webp" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">
              Commercial locksmiths & door hardware — the team the integrators trust
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              For years Auckland's security companies have used Shore Doors to deliver their
              installations. Property managers, facilities teams and businesses can now bring that
              same standard in directly.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
                Request a quote
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Track record strip */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-6">
            {featuredProjects.map((p) => (
              <div key={p.title} className="flex items-start gap-3">
                <MapPin className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                <div>
                  <p className="font-bold text-gray-900">{p.title}</p>
                  <p className="text-sm text-gray-600">{p.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/projects" className="text-blue-600 font-semibold text-sm">
              See all completed projects →
            </Link>
          </div>
        </div>
      </section>

      {/* Featured install video */}
      <section className="py-16 sm:py-20 bg-gray-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">See it in action</h2>
            <p className="text-lg text-gray-300">
              An extra-large swing door with electronic V-lock — one of our commercial installs.
            </p>
          </div>
          <video
            controls
            playsInline
            preload="metadata"
            src="/videos/xl-swing-door-vlock.mp4"
            className="rounded-2xl shadow-lg mx-auto max-w-full max-h-[70vh] bg-black"
          />
        </div>
      </section>

      {/* Service cards */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Commercial services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Service area: {siteConfig.commercialArea.toLowerCase()} — single doors to multi-building campuses
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {commercialCards.map((c) => {
              const cfg = servicePages.commercial[c.slug];
              const Icon = iconMap[c.icon] || Lock;
              const comingSoon = cfg.gated && !siteConfig.locksmithLive;
              const inner = (
                <CardContent className="p-7 flex flex-col h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="text-blue-600" size={24} />
                    </div>
                    {comingSoon && (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">
                        <Clock size={12} /> Launching soon
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{cfg.title}</h3>
                  <p className="text-gray-600 text-sm mb-5 flex-1">{c.blurb}</p>
                  {comingSoon ? (
                    <span className="text-gray-400 font-semibold text-sm">Available once our locksmith starts</span>
                  ) : (
                    <span className="text-blue-600 font-semibold flex items-center gap-2 text-sm">
                      Learn more <ArrowRight size={15} />
                    </span>
                  )}
                </CardContent>
              );
              return comingSoon ? (
                <Card key={c.slug} className="border-gray-200 bg-white/70">{inner}</Card>
              ) : (
                <Link key={c.slug} to={`/commercial/${c.slug}`} className="block group">
                  <Card className="border-gray-200 h-full bg-white group-hover:border-blue-300 group-hover:shadow-lg transition-all duration-300">{inner}</Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance + maintenance */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-5">Compliance handled properly</h2>
            <ul className="space-y-3">
              {[
                'All work meets NZ building codes and fire safety requirements',
                'Fire-door hardware installed to code and insurance requirements',
                'Compliant egress — panic and exit hardware done right',
                'IQP compliance support and inspections for specified systems',
                'As-built records and handover documentation on every project',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <Card className="border-blue-200 bg-blue-50 h-fit">
            <CardContent className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Planned maintenance agreements</h3>
              <p className="text-gray-700 mb-5">
                Doors are the hardest-working moving parts in your building. A scheduled
                maintenance agreement keeps hardware compliant and working — and remedial
                surprises off your budget.
              </p>
              <Link to="/commercial/maintenance-iqp">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-100">
                  Maintenance & IQP support
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust strip — badges + brands we install */}
      <TrustStrip />

      {/* CTA */}
      <section className="py-14 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Have a site or a portfolio in mind?</h2>
          <p className="text-lg text-gray-300 mb-7">
            Tell us about the doors and we'll come back with a clear, itemised quote.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold">
              Request a quote
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Commercial;
